// Centralized SEO utilities for the CSSBuy Spreadsheet site.
// Head-tag management (title/description/canonical/OG/Twitter) is kept separate
// from Schema.org JSON-LD injection.

export const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) || '';

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';

export const DEFAULT_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Curated%20premium%20fashion%20flatlay%20featuring%20designer%20sneakers%20hoodies%20caps%20and%20leather%20accessories%20on%20a%20soft%20neutral%20studio%20background%20high%20end%20editorial%20product%20photography%20soft%20natural%20light%20warm%20tones&width=1920&height=1080&seq=hero-fashion-01&orientation=landscape';

export function buildOrigin(): string {
  return SITE_URL ? SITE_URL.replace(/\/+$/, '') : '';
}

function ensureMeta(attr: 'name' | 'property', key: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function ensureLink(rel: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
}

export interface SeoInput {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
}

export function applySeoHead(input: SeoInput): void {
  const { title, description, keywords, path, type = 'website', image } = input;

  document.title = title;

  const origin = buildOrigin();
  const canonical = origin ? `${origin}${path === '/' ? '/' : path}` : path;
  ensureLink('canonical').setAttribute('href', canonical);

  ensureMeta('name', 'description').setAttribute('content', description);
  ensureMeta('name', 'keywords').setAttribute('content', keywords);

  ensureMeta('property', 'og:title').setAttribute('content', title);
  ensureMeta('property', 'og:description').setAttribute('content', description);
  ensureMeta('property', 'og:type').setAttribute('content', type);
  ensureMeta('property', 'og:url').setAttribute('content', canonical);
  ensureMeta('name', 'twitter:card').setAttribute('content', 'summary_large_image');
  ensureMeta('name', 'twitter:title').setAttribute('content', title);
  ensureMeta('name', 'twitter:description').setAttribute('content', description);

  const ogImage = image || DEFAULT_OG_IMAGE;
  ensureMeta('property', 'og:image').setAttribute('content', ogImage);
  ensureMeta('name', 'twitter:image').setAttribute('content', ogImage);
}

export function injectJsonLd(id: string, data: object | null): void {
  const existing = document.getElementById(id);
  if (data === null) {
    if (existing) existing.remove();
    return;
  }
  const script = (existing as HTMLScriptElement | null) || document.createElement('script');
  if (!existing) {
    script.id = id;
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function buildWebSiteSchema(
  name: string,
  description: string,
  url: string,
  inLanguage: string,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
    description,
    inLanguage,
    publisher: {
      '@type': 'Organization',
      name,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
  };
}

export function buildArticleSchema(opts: {
  headline: string;
  description: string;
  image?: string;
  author: string;
  publisherName: string;
  dateModified: string;
  url: string;
  inLanguage: string;
}) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    author: { '@type': 'Organization', name: opts.author },
    publisher: {
      '@type': 'Organization',
      name: opts.publisherName,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    datePublished: opts.dateModified,
    dateModified: opts.dateModified,
    mainEntityOfPage: { '@type': 'WebPage', '@id': opts.url },
    inLanguage: opts.inLanguage,
  };
  if (opts.image) {
    schema.image = [opts.image];
  }
  return schema;
}